

import { SectionCard } from "./SectionCard";
import { Financials, VendorTotals, ServiceTotals, ExpenseRow, fmt } from "./types";
import { useAuth } from "@/hooks/useAuth";

interface CostingSummaryProps {
  financials: Financials;
  vendorTotals: VendorTotals;
  serviceTotals: ServiceTotals;
  expenses: ExpenseRow[];
}

export function CostingSummary({ financials, vendorTotals, serviceTotals, expenses }: CostingSummaryProps) {
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  
  const f = financials;
  const profit = f.profit ?? 0;
  const netRevenue = f.net_revenue ?? 0;
  const profitMargin = netRevenue > 0 ? ((profit / netRevenue) * 100).toFixed(1) : "0.0";

  return (
    <SectionCard
      title="Event Costing Summary"
      icon={
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 3.666V14m-6.881 2.683l6.879-2.683m-6.879 2.683l6.879 2.683M12 3v1m0 16v1m8-9h1M4 12H3m16.364-6.364l-.707.707M6.343 17.657l-.707.707M18.364 17.657l-.707-.707M6.343 6.343l-.707-.707" />
        </svg>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Side */}
        <div className="bg-emerald-50/50 rounded-xl p-4 border border-emerald-100">
          <h4 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 11l5-5m0 0l5 5m-5-5v12" />
            </svg>
            Revenue (Inflow)
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-stone-600">Invoice Subtotal</span>
              <span className="font-medium text-stone-800">{fmt(f.income_subtotal)}</span>
            </div>
            {f.discount_amount > 0 && (
              <div className="flex justify-between text-rose-600">
                <span>Discount</span>
                <span>- {fmt(f.discount_amount)}</span>
              </div>
            )}
            <div className="border-t border-emerald-200 pt-2 flex justify-between font-semibold">
              <span className="text-emerald-800">Net Revenue</span>
              <span className="text-emerald-700">{fmt(f.net_revenue)}</span>
            </div>
            <div className="flex justify-between text-xs text-stone-500">
              <span>Received from Client</span>
              <span>{fmt(f.customer?.paid ?? 0)}</span>
            </div>
            <div className="flex justify-between text-xs font-medium">
              <span className={f.customer?.balance > 0 ? 'text-rose-600' : 'text-emerald-600'}>
                {f.customer?.balance > 0 ? 'Balance Due' : 'Fully Paid'}
              </span>
              <span className={f.customer?.balance > 0 ? 'text-rose-600' : 'text-emerald-600'}>
                {fmt(f.customer?.balance ?? 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Cost Side */}
        <div className="bg-rose-50/50 rounded-xl p-4 border border-rose-100">
          <h4 className="text-sm font-semibold text-rose-800 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 13l-5 5m0 0l-5-5m5 5V6" />
            </svg>
            Costs (Outflow)
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-stone-600">Managed Services (COGS)</span>
              <span className="font-medium text-stone-800">{fmt(serviceTotals.total_cost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-600">Vendor Outsourcing</span>
              <span className="font-medium text-stone-800">{fmt(vendorTotals.total_assigned)}</span>
            </div>
            {expenses.length > 0 && (
              <div className="flex justify-between">
                <span className="text-stone-600">Additional Expenses</span>
                <span className="font-medium text-stone-800">{fmt(f.adhoc_cost)}</span>
              </div>
            )}
            <div className="border-t border-rose-200 pt-2 flex justify-between font-semibold">
              <span className="text-rose-800">Total Costs</span>
              <span className="text-rose-700">{fmt(f.total_cost + f.adhoc_cost)}</span>
            </div>
            <div className="flex justify-between text-xs text-stone-500">
              <span>Paid to Vendors</span>
              <span className="text-emerald-600">{fmt(vendorTotals.total_paid)}</span>
            </div>
            <div className="flex justify-between text-xs font-medium">
              <span className={vendorTotals.total_balance > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                {vendorTotals.total_balance > 0 ? 'Pending to Pay' : 'All Paid'}
              </span>
              <span className={vendorTotals.total_balance > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                {fmt(vendorTotals.total_balance)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Net Result - Hidden for staff */}
      {!isStaff && (
        <div className={`mt-4 rounded-xl p-4 border ${profit >= 0 ? 'bg-emerald-100 border-emerald-200' : 'bg-rose-100 border-rose-200'}`}>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div>
              <div className={`text-sm font-semibold ${profit >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                {profit >= 0 ? 'Event is Profitable' : 'Event is at Loss'}
              </div>
              <div className={`text-xs ${profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {profit >= 0 
                  ? `You are earning ${profitMargin}% profit margin on this event`
                  : `You are losing money on this event. Review costs.`}
              </div>
            </div>
            <div className={`text-xl sm:text-2xl font-bold ${profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
              {fmt(Math.abs(profit))}
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
